/**
 * People & Capacity — staffing grid for the workspace.
 *
 * Three views:
 *   Roster     — dense table, all members visible, hours editable directly in-cell,
 *                capabilities shown as compact chips with quick-add popover.
 *   Matrix     — person × capability grid; set skill levels across the whole team at once.
 *   By Skill   — capability-first; see who holds each skill and aggregate hours.
 *
 * Design principle: treat this like a spreadsheet, not a form. Every person is always
 * visible. Every editable field is editable in-place. No expand/collapse patterns.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Users, Layers, LayoutGrid, Plus, X, ChevronDown, UserPlus, Briefcase } from 'lucide-react';
import { getItemCountState } from '../../components/collection/types';
import { PageHeader } from '../../components/prioritization/PageHeader';
import {
  listCapabilities,
  listIndividualCapabilitiesByWorkspace,
  listIndividualCapacity,
  upsertIndividualCapability,
  deleteIndividualCapability,
  upsertIndividualCapacity,
} from '../../lib/capabilityService';
import { listWorkspaceMembers } from '../../lib/workspaceService';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useResourceRosterEnabled } from '../../hooks/useResourceRosterEnabled';
import PlanningRolesView from './PlanningRolesView';
import { useResourceTypesStore } from '../../stores/prioritization/resourceTypesStore';
import type {
  Capability,
  IndividualCapability,
  IndividualCapacity,
} from '../../types/capabilities';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Member {
  userId: string;
  name: string;
  email: string;
  role: string;
  initials: string;
}

type View = 'roster' | 'roles' | 'matrix' | 'by-skill';

// ─── Inline hours cell ────────────────────────────────────────────────────────
// Always visible number input — saves on blur, no button required.

function HoursInput({
  userId,
  workspaceId,
  value,
  onChange,
}: {
  userId: string;
  workspaceId: string;
  value: number | null;
  onChange: (hours: number) => void;
}) {
  const [draft, setDraft] = useState(value != null ? String(value) : '');

  useEffect(() => {
    setDraft(value != null ? String(value) : '');
  }, [value]);

  const save = async () => {
    const h = parseFloat(draft);
    if (isNaN(h) || h < 0) return;
    onChange(h);
    await upsertIndividualCapacity({
      user_id: userId,
      workspace_id: workspaceId,
      available_hours_per_week: h,
    });
  };

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        step={1}
        value={draft}
        placeholder="—"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="w-16 px-2 py-1 text-sm text-right border border-[var(--rule)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none bg-[var(--bg-raised)]"
      />
      <span className="text-xs text-[var(--text-ter)] whitespace-nowrap">h/wk</span>
    </div>
  );
}

// ─── Capability chip with remove ──────────────────────────────────────────────

function CapChip({
  name,
  level,
  onRemove,
}: {
  name: string;
  level: number;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100 group">
      {name}
      <span className="text-primary-400">·{level}</span>
      <button
        type="button"
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 hover:text-[var(--coral)]"
        aria-label={`Remove ${name}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─── Add-capability popover ───────────────────────────────────────────────────

function AddCapPopover({
  userId,
  capabilities,
  assigned,
  onAdded,
  onClose,
  anchorRef,
}: {
  userId: string;
  capabilities: Capability[];
  assigned: IndividualCapability[];
  onAdded: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const unassigned = capabilities.filter(
    (c) => !assigned.some((a) => a.capability_id === c.id),
  );
  const [selected, setSelected] = useState('');
  const [level, setLevel] = useState(5);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    await upsertIndividualCapability({ user_id: userId, capability_id: selected, level });
    setSaving(false);
    onAdded();
    onClose();
  };

  if (unassigned.length === 0) {
    return (
      <div ref={ref} className="absolute z-50 top-full left-0 mt-1 w-56 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg p-3 text-sm text-[var(--text-ter)]">
        All skills assigned.
      </div>
    );
  }

  return (
    <div ref={ref} className="absolute z-50 top-full left-0 mt-1 w-64 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg p-3 space-y-3">
      <div>
        <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Skill</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full text-sm px-2 py-1.5 border border-[var(--rule)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 outline-none bg-[var(--bg-raised)]"
        >
          <option value="">Choose…</option>
          {unassigned.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">
          Level <span className="text-[var(--text-ter)] font-normal">(1–10)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={1}
            max={10}
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
            className="flex-1 accent-primary-600"
          />
          <span className="text-sm font-medium text-[var(--text-sec)] w-4 text-right">{level}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-3 py-1.5 border border-[var(--rule)] text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={!selected || saving}
          className="flex-1 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ─── Roster view ─────────────────────────────────────────────────────────────

function RosterView({
  members,
  capabilities,
  capsByUser,
  capacityByUser,
  workspaceId,
  onRefresh,
  onHoursChange,
}: {
  members: Member[];
  capabilities: Capability[];
  capsByUser: Record<string, IndividualCapability[]>;
  capacityByUser: Record<string, number>;
  workspaceId: string;
  onRefresh: () => void;
  onHoursChange: (userId: string, hours: number) => void;
}) {
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const btnRefs = useRef<Record<string, React.RefObject<HTMLButtonElement | null>>>({});

  const getBtnRef = (userId: string) => {
    if (!btnRefs.current[userId]) {
      btnRefs.current[userId] = { current: null };
    }
    return btnRefs.current[userId];
  };

  const handleRemove = async (ic: IndividualCapability) => {
    await deleteIndividualCapability(ic.id);
    onRefresh();
  };

  return (
    // Scrolls sideways on a phone: below ~36rem the columns crush the names.
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-x-auto">
      {/* Column headers */}
      <div className="grid min-w-[36rem] grid-cols-[2fr_1fr_3fr_auto] gap-4 px-4 py-2 border-b border-[var(--rule)] bg-[var(--surface)]">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Person</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Role</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Skills</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] text-right pr-2">Hrs/wk</span>
      </div>

      {/* §1E zero state — remediation points to workspace membership settings */}
      {members.length === 0 && (
        <div className="px-6 py-14 flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--hover-tint)]">
            <UserPlus className="h-6 w-6 text-[var(--text-ter)]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[var(--text-sec)]">No active workspace members</p>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              Add members from Workspace Settings, then return here to configure capacity and skills.
            </p>
          </div>
        </div>
      )}

      {/* Rows */}
      {members.map((m, idx) => {
        const memberCaps = capsByUser[m.userId] ?? [];
        const hours = capacityByUser[m.userId] ?? null;
        const btnRef = getBtnRef(m.userId);

        return (
          <div
            key={m.userId}
            className={`grid min-w-[36rem] grid-cols-[2fr_1fr_3fr_auto] gap-4 px-4 py-3 items-center ${
              idx % 2 === 0 ? '' : 'bg-[var(--surface)]/40'
            } border-b border-[var(--rule)] last:border-0`}
          >
            {/* Person */}
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex-shrink-0">
                {m.initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[var(--text-pri)] truncate">{m.name || m.email}</p>
                {m.name && <p className="text-xs text-[var(--text-ter)] truncate">{m.email}</p>}
              </div>
            </div>

            {/* Role */}
            <span className="text-xs text-[var(--text-ter)] capitalize">{m.role}</span>

            {/* Skills */}
            <div className="flex flex-wrap gap-1.5 items-center relative">
              {memberCaps.map((ic) => {
                const cap = capabilities.find((c) => c.id === ic.capability_id);
                return (
                  <CapChip
                    key={ic.id}
                    name={cap?.name ?? '?'}
                    level={ic.level}
                    onRemove={() => void handleRemove(ic)}
                  />
                );
              })}
              <div className="relative">
                <button
                  ref={btnRef as React.RefObject<HTMLButtonElement>}
                  type="button"
                  onClick={() => setOpenPopover(openPopover === m.userId ? null : m.userId)}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs text-[var(--text-ter)] border border-dashed border-[var(--rule)] hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  <Plus className="h-3 w-3" />
                </button>
                {openPopover === m.userId && (
                  <AddCapPopover
                    userId={m.userId}
                    capabilities={capabilities}
                    assigned={memberCaps}
                    onAdded={onRefresh}
                    onClose={() => setOpenPopover(null)}
                    anchorRef={btnRef}
                  />
                )}
              </div>
            </div>

            {/* Hours */}
            <div className="flex justify-end">
              <HoursInput
                userId={m.userId}
                workspaceId={workspaceId}
                value={hours}
                onChange={(h) => onHoursChange(m.userId, h)}
              />
            </div>
          </div>
        );
      })}

    </div>
  );
}

// ─── Matrix view ──────────────────────────────────────────────────────────────
// Rows = people, columns = capabilities. Click any cell to set/clear skill level.

function MatrixCell({
  userId,
  capabilityId,
  existing,
  onSaved,
  onRemoved,
}: {
  userId: string;
  capabilityId: string;
  existing: IndividualCapability | undefined;
  onSaved: (ic: IndividualCapability) => void;
  onRemoved: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [level, setLevel] = useState(existing?.level ?? 5);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLevel(existing?.level ?? 5);
  }, [existing?.level]);

  const save = async (l: number) => {
    setSaving(true);
    const { data } = await upsertIndividualCapability({ user_id: userId, capability_id: capabilityId, level: l });
    setSaving(false);
    if (data) onSaved(data);
    setEditing(false);
  };

  const remove = async () => {
    if (!existing) return;
    setSaving(true);
    await deleteIndividualCapability(existing.id);
    setSaving(false);
    onRemoved(existing.id);
    setEditing(false);
  };

  if (editing) {
    return (
      <td className="px-2 py-1.5 border-r border-[var(--rule)] last:border-0 bg-[var(--slate-dim)]/60 min-w-[80px]">
        <div className="flex flex-col items-center gap-1">
          <input
            type="range"
            min={1}
            max={10}
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
            className="w-full accent-primary-600"
            autoFocus
          />
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void save(level)}
              disabled={saving}
              className="px-2 py-0.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 disabled:opacity-40"
            >
              {level}✓
            </button>
            {existing && (
              <button
                type="button"
                onClick={() => void remove()}
                disabled={saving}
                className="px-1.5 py-0.5 border border-[var(--rule)] rounded text-xs text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-40"
              >
                ✕
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-1.5 py-0.5 border border-[var(--rule)] rounded text-xs text-[var(--text-ter)] hover:bg-[var(--surface)]"
            >
              esc
            </button>
          </div>
        </div>
      </td>
    );
  }

  return (
    <td
      className={`px-2 py-2 border-r border-[var(--rule)] last:border-0 text-center cursor-pointer min-w-[64px] group transition-colors ${
        existing ? 'hover:bg-primary-50' : 'hover:bg-[var(--surface)]'
      }`}
      onClick={() => setEditing(true)}
      title={existing ? `Level ${existing.level} — click to edit` : 'Click to assign'}
    >
      {existing ? (
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold group-hover:bg-primary-200 transition-colors">
          {existing.level}
        </span>
      ) : (
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full text-[var(--text-ter)] group-hover:text-[var(--text-ter)] group-hover:bg-[var(--hover-tint)] transition-colors text-xs">
          +
        </span>
      )}
    </td>
  );
}

function MatrixView({
  members,
  capabilities,
  individualCaps,
  onCapChange,
}: {
  members: Member[];
  capabilities: Capability[];
  individualCaps: IndividualCapability[];
  onCapChange: (updated: IndividualCapability[], removedId?: string) => void;
}) {
  const lookup = useMemo(
    () =>
      individualCaps.reduce<Record<string, IndividualCapability>>((acc, ic) => {
        acc[`${ic.user_id}:${ic.capability_id}`] = ic;
        return acc;
      }, {}),
    [individualCaps],
  );

  const handleSaved = (ic: IndividualCapability) => {
    const key = `${ic.user_id}:${ic.capability_id}`;
    const next = individualCaps.filter((x) => `${x.user_id}:${x.capability_id}` !== key);
    onCapChange([...next, ic]);
  };

  const handleRemoved = (removedId: string) => {
    onCapChange(
      individualCaps.filter((x) => x.id !== removedId),
      removedId,
    );
  };

  if (capabilities.length === 0) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-6 py-10 text-center text-sm text-[var(--text-ter)]">
        No skills defined yet. Add skills from workspace settings.
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--rule)] bg-[var(--surface)]">
            <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] whitespace-nowrap sticky left-0 bg-[var(--surface)] border-r border-[var(--rule)] min-w-[200px]">
              Person
            </th>
            {capabilities.map((c) => (
              <th
                key={c.id}
                className="px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] whitespace-nowrap border-r border-[var(--rule)] last:border-0 min-w-[64px]"
                title={c.description ?? c.name}
              >
                <span className="block truncate max-w-[80px]">{c.name}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map((m, idx) => (
            <tr
              key={m.userId}
              className={`border-b border-[var(--rule)] last:border-0 ${idx % 2 === 1 ? 'bg-[var(--surface)]/30' : ''}`}
            >
              <td className="px-4 py-2 sticky left-0 bg-[var(--bg-raised)] border-r border-[var(--rule)] min-w-[200px]">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex-shrink-0">
                    {m.initials}
                  </div>
                  <span className="text-sm font-medium text-[var(--text-pri)] truncate">{m.name || m.email}</span>
                </div>
              </td>
              {capabilities.map((c) => (
                <MatrixCell
                  key={c.id}
                  userId={m.userId}
                  capabilityId={c.id}
                  existing={lookup[`${m.userId}:${c.id}`]}
                  onSaved={handleSaved}
                  onRemoved={handleRemoved}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── By-Skill view ────────────────────────────────────────────────────────────

function BySkillView({
  capabilities,
  members,
  individualCaps,
  capacityByUser,
}: {
  capabilities: Capability[];
  members: Member[];
  individualCaps: IndividualCapability[];
  capacityByUser: Record<string, number>;
}) {
  const [open, setOpen] = useState<string | null>(null);

  const capsByCap = useMemo(
    () =>
      individualCaps.reduce<Record<string, IndividualCapability[]>>((acc, ic) => {
        (acc[ic.capability_id] ??= []).push(ic);
        return acc;
      }, {}),
    [individualCaps],
  );

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-x-auto">
      <div className="grid min-w-[36rem] grid-cols-[3fr_1fr_1fr_1fr] gap-4 px-4 py-2 border-b border-[var(--rule)] bg-[var(--surface)]">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Skill</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] text-right">People</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] text-right">Avg level</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] text-right">Total h/wk</span>
      </div>
      {capabilities.map((cap, idx) => {
        const holders = capsByCap[cap.id] ?? [];
        const totalHours = holders.reduce((s, ic) => s + (capacityByUser[ic.user_id] ?? 0), 0);
        const avgLevel = holders.length > 0 ? Math.round(holders.reduce((s, ic) => s + ic.level, 0) / holders.length) : 0;
        const isOpen = open === cap.id;

        return (
          <div key={cap.id} className={`border-b border-[var(--rule)] last:border-0 ${idx % 2 === 1 ? 'bg-[var(--surface)]/40' : ''}`}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : cap.id)}
              className="w-full grid min-w-[36rem] grid-cols-[3fr_1fr_1fr_1fr] gap-4 px-4 py-3 items-center text-left hover:bg-[var(--surface)] transition-colors"
            >
              <div className="flex items-center gap-2">
                <ChevronDown className={`h-3.5 w-3.5 text-[var(--text-ter)] transition-transform flex-shrink-0 ${isOpen ? '' : '-rotate-90'}`} />
                <div>
                  <p className="text-sm font-medium text-[var(--text-pri)]">{cap.name}</p>
                  {cap.description && <p className="text-xs text-[var(--text-ter)] truncate">{cap.description}</p>}
                </div>
              </div>
              <span className="text-sm text-[var(--text-sec)] text-right font-medium">{holders.length}</span>
              <span className="text-sm text-[var(--text-sec)] text-right">{avgLevel > 0 ? `${avgLevel}/10` : '—'}</span>
              <span className="text-sm text-[var(--text-sec)] text-right">{totalHours > 0 ? `${totalHours}h` : '—'}</span>
            </button>
            {isOpen && (
              <div className="px-10 pb-3 space-y-1">
                {holders.length === 0 ? (
                  <p className="text-xs text-[var(--text-ter)] py-1">No one assigned yet.</p>
                ) : (
                  holders.map((ic) => {
                    const m = members.find((x) => x.userId === ic.user_id);
                    return (
                      <div key={ic.id} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <div className="h-5 w-5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                            {m?.initials ?? '?'}
                          </div>
                          <span className="text-sm text-[var(--text-sec)]">{m?.name || m?.email || ic.user_id.slice(0, 8)}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[var(--text-ter)]">
                          <span>Level {ic.level}/10</span>
                          <span>{capacityByUser[ic.user_id] != null ? `${capacityByUser[ic.user_id]}h/wk` : '—'}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
      {capabilities.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-[var(--text-ter)]">No skills defined yet.</div>
      )}
    </div>
  );
}

// ─── View toggle ─────────────────────────────────────────────────────────────

const VIEWS: { id: View; label: string; icon: React.ElementType }[] = [
  { id: 'roster',   label: 'Roster',    icon: Users },
  // Roles is the join that makes the rest of this page reach a plan; it only
  // appears when the roster module is on.
  { id: 'roles',    label: 'Roles',     icon: Briefcase },
  { id: 'matrix',   label: 'Matrix',    icon: LayoutGrid },
  { id: 'by-skill', label: 'By skill',  icon: Layers },
];

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlanningPeopleCapacity() {
  const [view, setView] = useState<View>('roster');
  const rosterEnabled = useResourceRosterEnabled();
  const [members, setMembers] = useState<Member[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [individualCaps, setIndividualCaps] = useState<IndividualCapability[]>([]);
  const [capacities, setCapacities] = useState<IndividualCapacity[]>([]);
  const [loading, setLoading] = useState(true);

  // Use reactive store so a hard-refresh (which restores auth asynchronously)
  // triggers a re-render once the workspace is available, causing load() to fire.
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? '';

  const load = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [membersRes, capsRes, indCapsRes, capacityRes] = await Promise.all([
        listWorkspaceMembers(workspaceId),
        listCapabilities(workspaceId),
        listIndividualCapabilitiesByWorkspace(workspaceId),
        listIndividualCapacity(workspaceId),
      ]);

      setMembers(
        (membersRes ?? [])
          .filter((m) => m.status === 'active')
          .map((m) => {
            const display = m.user?.name || m.user?.email || '';
            return {
              userId: m.userId,
              name: m.user?.name ?? '',
              email: m.user?.email ?? '',
              role: m.role,
              initials: display.slice(0, 1).toUpperCase(),
            };
          }),
      );
      setCapabilities(capsRes.data ?? []);
      setIndividualCaps(indCapsRes.data ?? []);
      setCapacities(capacityRes.data ?? []);
    } catch {
      // non-fatal — tables may not exist in all workspaces
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { void load(); }, [load]);

  // The Roles view names resource types and offers them in its picker.
  useEffect(() => {
    if (!workspaceId || !rosterEnabled) return;
    void useResourceTypesStore.getState().load(workspaceId);
  }, [workspaceId, rosterEnabled]);

  const capacityByUser = useMemo(
    () => Object.fromEntries(capacities.map((c) => [c.user_id, Number(c.available_hours_per_week)])),
    [capacities],
  );

  const capsByUser = useMemo(
    () =>
      individualCaps.reduce<Record<string, IndividualCapability[]>>((acc, ic) => {
        (acc[ic.user_id] ??= []).push(ic);
        return acc;
      }, {}),
    [individualCaps],
  );

  const handleHoursChange = (userId: string, hours: number) => {
    setCapacities((prev) => {
      const existing = prev.find((c) => c.user_id === userId);
      if (existing) {
        return prev.map((c) => c.user_id === userId ? { ...c, available_hours_per_week: hours } : c);
      }
      const now = new Date().toISOString();
      return [...prev, {
        id: '',
        user_id: userId,
        workspace_id: workspaceId,
        available_hours_per_week: hours,
        effective_from: null,
        effective_to: null,
        created_at: now,
        updated_at: now,
      }];
    });
  };

  const totalHours = capacities.reduce((s, c) => s + Number(c.available_hours_per_week), 0);
  const countState = getItemCountState(members.length);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-6 w-6 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<Users className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="People & Capacity"
        subtitle={
          countState === 'zero'
            ? 'No active members — add members from Workspace Settings to get started'
            : `${members.length} member${members.length !== 1 ? 's' : ''} · ${totalHours.toFixed(0)} h/wk total`
        }
      />

      {/* View toggle */}
      <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)] w-fit max-w-full overflow-x-auto">
        {VIEWS.filter((v) => v.id !== 'roles' || rosterEnabled).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className={`flex items-center gap-2 px-3 py-1.5  text-sm font-medium transition-all ${
              view === id
                ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {view === 'roster' && (
        <RosterView
          members={members}
          capabilities={capabilities}
          capsByUser={capsByUser}
          capacityByUser={capacityByUser}
          workspaceId={workspaceId}
          onRefresh={load}
          onHoursChange={handleHoursChange}
        />
      )}

      {view === 'roles' && rosterEnabled && (
        <PlanningRolesView workspaceId={workspaceId} members={members} />
      )}

      {view === 'matrix' && (
        <>
          <p className="text-xs text-[var(--text-ter)]">
            Click any cell to assign or edit a skill level. Click the level badge again to adjust or remove.
          </p>
          <MatrixView
            members={members}
            capabilities={capabilities}
            individualCaps={individualCaps}
            onCapChange={(updated) => setIndividualCaps(updated)}
          />
        </>
      )}

      {view === 'by-skill' && (
        <BySkillView
          capabilities={capabilities}
          members={members}
          individualCaps={individualCaps}
          capacityByUser={capacityByUser}
        />
      )}
    </div>
  );
}
